import dayjs from "dayjs";
import { type BillingProfileOption, type OnboardingAnswers } from "../../types/onboarding";
import { domainMessage, emailMessage } from "../../utils/storefrontFields";
import { findStackBrandIndexes } from "./stack";

export const SECTIONS = [
  { key: "client", label: "Client", hint: "Who signed and who we talk to." },
  { key: "brands", label: "Brands and processors", hint: "Tick every processor each brand uses. Each one adds its access steps for that brand." },
  { key: "services", label: "What they bought", hint: "Alerts, representment and anything extra agreed." },
  { key: "stack", label: "Their stack", hint: "Per brand: CRM, subscription app, where refunds are issued, and the tools we need for representment." },
  { key: "review", label: "Review and create", hint: "Check the checklist this will create." },
] as const;

export type SectionKey = typeof SECTIONS[number]["key"];

// `field` names the input that goes red once its section was left.
export type Missing = { section: SectionKey; label: string; field?: string };
type Rule = [section: SectionKey, label: string, isMissing: boolean, field?: string];

const blank = (value: string | undefined) => !value?.trim();
const toMissing = (rules: Rule[]): Missing[] => rules.filter(([, , isMissing]) => isMissing).map(([section, label, , field]) => ({ section, label, field }));

// With several brands answering the stack, each label names its brand.
export function listStackMissing(a: OnboardingAnswers): Missing[] {
  const stackBrands = findStackBrandIndexes(a.brands);
  return toMissing(stackBrands.flatMap((i): Rule[] => {
    const s = a.brands[i].stack;
    const forBrand = stackBrands.length > 1 ? ` for ${a.brands[i].name.trim() || `brand ${i + 1}`}` : "";
    const rule = (label: string, isMissing: boolean, field: string): Rule => ["stack", `${label}${forBrand}`, isMissing, `brands.${i}.stack.${field}`];
    const fulfilment = s.fulfilment ?? [];
    const emailMarketing = s.emailMarketing ?? [];
    return [
      rule("CRM", s.crms.length === 0, "crms"),
      rule("Subscription apps", s.subscriptionApps.length === 0, "subscriptionApps"),
      rule("Helpdesk", a.representment && s.helpdesks.length === 0, "helpdesks"),
      rule("Fulfilment", a.representment && fulfilment.length === 0, "fulfilment"),
      rule("Email marketing", a.representment && emailMarketing.length === 0, "emailMarketing"),
      rule("Where refunds are issued", s.refundsIn.length === 0 || (a.alerts.enabled && s.refundsIn.includes("none")), "refundsIn"),
      rule("Other refund platform name", s.refundsIn.includes("other") && blank(s.refundsOtherName), "refundsOtherName"),
      rule("Other CRM name", s.crms.includes("other") && blank(s.otherCrmName), "otherCrmName"),
      rule("Other subscription app name", s.subscriptionApps.includes("other") && blank(s.otherSubscriptionAppName), "otherSubscriptionAppName"),
      rule("Other helpdesk name", a.representment && s.helpdesks.includes("other") && blank(s.otherHelpdeskName), "otherHelpdeskName"),
      rule("Other fulfilment tool name", a.representment && fulfilment.includes("other") && blank(s.otherFulfilmentName), "otherFulfilmentName"),
      rule("Other email marketing name", a.representment && emailMarketing.includes("other") && blank(s.otherEmailMarketingName), "otherEmailMarketingName"),
    ];
  }));
}

// The API's required-field rules (onboardingSchemas.ts), each with the section that fixes it.
export function listMissing(a: OnboardingAnswers, isExisting: boolean, billingProfiles: BillingProfileOption[]): Missing[] {
  const names = a.brands.map((b) => b.name.trim().toLowerCase());
  const brandName = (i: number) => a.brands[i].name.trim() || `brand ${i + 1}`;
  const rules: Rule[] = [
    ["client", "Company name", blank(a.companyName), "companyName"],
    // An existing company may sign nothing new; a date given must still be real and not ahead.
    ["client", "Legal entity", !isExisting && blank(a.legalEntity), "legalEntity"],
    ["client", "Signed on date", (!isExisting || a.signedOn !== "") && (dayjs(a.signedOn).format("YYYY-MM-DD") !== a.signedOn || a.signedOn > dayjs.utc().format("YYYY-MM-DD")), "signedOn"],
    ["client", "Admin first name", blank(a.admin.firstName), "admin.firstName"],
    ["client", "Admin last name", blank(a.admin.lastName), "admin.lastName"],
    ["client", "Admin email", blank(a.admin.email), "admin.email"],
    ["client", "Admin email", !blank(a.admin.email) && emailMessage(a.admin.email) !== null, "admin.emailFormat"],
    ["brands", "At least one brand", a.brands.length === 0],
    ["brands", "A processor on at least one brand", !isExisting && !a.brands.some((b) => b.processors.length > 0)],
    ...a.brands.map((_, i): Rule => ["brands", `Name of brand ${i + 1}`, !names[i], `brands.${i}.name`]),
    ...a.brands.map((_, i): Rule => ["brands", `${brandName(i)} is listed twice`, !!names[i] && names.indexOf(names[i]) !== i, `brands.${i}.duplicate`]),
    ...a.brands.map((b, i): Rule => [
      "brands", `Other processor name for ${brandName(i)}`, b.processors.includes("other") && !b.otherProcessorNames?.length, `brands.${i}.otherProcessorNames`,
    ]),
    ...a.brands.map((b, i): Rule => ["brands", `Store URL for ${brandName(i)}`, !!b.storeUrl?.trim() && domainMessage(b.storeUrl) !== null, `brands.${i}.storeUrl`]),
    ...a.brands.map((b, i): Rule => [
      "brands", `Billing profile for ${brandName(i)}`, billingProfiles.length > 1 && b.brandId === undefined && b.billingProfileId === undefined, `brands.${i}.billingProfileId`,
    ]),
    ["services", "Alerts or representment", !a.alerts.enabled && !a.representment],
    ["stack", "Previous provider", a.switching.enabled && blank(a.switching.provider), "switching.provider"],
  ];
  return [...toMissing(rules), ...listStackMissing(a)];
}
